import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  Calendar, 
  Shield, 
  GitBranch, 
  BarChart3,
  ArrowRight,
  Check
} from 'lucide-react';

// Feature Detail Component
const FeatureBlock = ({ icon: Icon, title, description, benefits, index }) => (
  <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
    <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
      <div className="w-14 h-14 rounded-xl bg-indigo-500/20 flex items-center justify-center mb-6">
        <Icon className="w-7 h-7 text-indigo-400" strokeWidth={1.5} />
      </div>
      <h3 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4">
        {title}
      </h3>
      <p className="text-lg text-slate-300/80 mb-8">
        {description}
      </p>
      <ul className="space-y-4">
        {benefits.map((benefit, i) => (
          <li key={i} className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-emerald-400" strokeWidth={3} />
            </div>
            <span className="text-slate-300/90">{benefit}</span>
          </li>
        ))}
      </ul>
    </div>
    
    {/* Feature visualization */}
    <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
      {/* Glow */}
      <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/10 via-cyan-500/5 to-purple-500/10 rounded-3xl blur-2xl" />
      
      <div className="relative glass rounded-2xl p-8">
        {/* Abstract UI representation */}
        <div className="space-y-4">
          <div className="h-4 bg-white/10 rounded-full w-3/4" />
          <div className="h-4 bg-white/10 rounded-full w-1/2" />
          <div className="grid grid-cols-3 gap-3 mt-6">
            {[1, 2, 3].map((_, i) => (
              <div key={i} className="h-20 rounded-xl bg-white/5 border border-white/10" />
            ))}
          </div>
          <div className="h-32 rounded-xl bg-white/5 border border-white/10 mt-4" />
        </div>
      </div>
    </div>
  </div>
);

export default function FeaturesPage() {
  const features = [
    {
      icon: Zap,
      title: 'Lead Intake Infrastructure',
      description: 'Capture and structure lead data from any source with full attribution tracking.',
      benefits: [
        'Accept leads from funnels, forms, ads, and API integrations',
        'Automatic source and campaign attribution',
        'Structured data capture for insurance-specific fields',
        'Real-time lead validation and deduplication',
      ],
    },
    {
      icon: Calendar,
      title: 'Instant Booking Infrastructure',
      description: 'Convert leads into scheduled appointments with high-conversion booking flows.',
      benefits: [
        'One-click booking widget for funnels and landing pages',
        'Calendar sync with GoHighLevel and other platforms',
        'Smart slot prioritization for same-day bookings',
        'Automated confirmation and reminder workflows',
      ],
    },
    {
      icon: Shield,
      title: 'Compliance Infrastructure',
      description: 'Automatically capture and store compliance data for every lead.',
      benefits: [
        'TrustedForm certificate capture and storage',
        'Consent record management',
        'TCPA compliance documentation',
        'Audit-ready intake metadata',
      ],
    },
    {
      icon: GitBranch,
      title: 'Operational Workflows',
      description: 'Automate the repetitive tasks that slow down your agency.',
      benefits: [
        'Lead routing based on source, product, or agent',
        'Automated follow-up sequences',
        'Pipeline stage transitions and tagging',
        'Multi-agent assignment and load balancing',
      ],
    },
    {
      icon: BarChart3,
      title: 'Operational Visibility',
      description: 'Understand what is actually driving business in your agency.',
      benefits: [
        'Source-to-appointment conversion tracking',
        'Campaign performance analytics',
        'Agent productivity metrics',
        'Pipeline health and velocity reports',
      ],
    },
  ];

  return (
    <main>
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-indigo-500/15 rounded-full blur-[100px]" />
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-8">
              <span className="text-sm font-medium text-slate-300">Platform Features</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.1] mb-6">
              Infrastructure Built for Insurance Operations
            </h1>
            <p className="text-lg md:text-xl text-slate-300/80 leading-relaxed">
              Every feature is designed to help insurance agencies operate more efficiently and close more business.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8 space-y-32">
          {features.map((feature, i) => (
            <FeatureBlock key={i} {...feature} index={i} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="relative glass rounded-3xl p-12 md:p-20 overflow-hidden">
            {/* Glows */}
            <div className="absolute top-0 left-1/4 w-[300px] h-[300px] bg-indigo-500/20 rounded-full blur-[80px]" />
            
            <div className="relative text-center max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-6">
                See How MissionAssist Fits Your Agency
              </h2>
              <p className="text-lg text-slate-300/80 mb-10">
                Book a demo to walk through the workflows, lead flow, and operational setup most relevant to your business.
              </p>
              
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  to="/demo"
                  className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-indigo-500 text-white font-medium hover:bg-indigo-400 transition-all shadow-lg shadow-indigo-500/25"
                  data-testid="features-cta-demo"
                >
                  Book a Demo
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-white/5 text-white font-medium border border-white/10 hover:bg-white/10 transition-all"
                >
                  Log In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
